/**
 * Relewise product listing — collection pages and the search page (Theme settings → Relewise → Collections /
 * Search). Relewise decides which products show, their order and the filters; the page keeps the theme's markup.
 *
 * - Collection pages run a termless search filtered on the collection (Relewise go-live check 2026-09-15:
 *   category navigation is a search with filters). The search page runs a term search.
 * - Filters and sorting use Shopify's own URL parameters (filter.p.product_type=…, filter.v.option.størrelse=…,
 *   sort_by=…, page=…): menu links and bookmarks keep working, and the server-rendered Shopify result — kept for
 *   crawlers and as the fallback — shows the same selection.
 * - Cards are the theme's own (sections/relewise-cards, in this collection so partner prices apply), in
 *   Relewise's order. Breaker and featured-image tiles keep their slots.
 * - The server-rendered result stays hidden until Relewise has answered (assets/relewise.css); if Relewise fails,
 *   it is simply shown. There is no timer: a slow answer is waited for.
 * - No results: the page's lazy Relewise rows load (relewise:no-results → assets/relewise-rows.js).
 */
(() => {
  const listing = document.querySelector('[data-relewise-listing]');
  if (!listing) return;
  const core = window.agf && window.agf.relewiseCore;
  const cfg = window.agf && window.agf.relewise;
  const grid = listing.querySelector('#CollectionLoop, #SearchLoop');
  const ready = () => listing.setAttribute('data-relewise-ready', '');
  if (!core || !cfg || !grid) {
    ready();
    return;
  }

  const isSearch = listing.dataset.relewiseListing === 'search';
  const pageSize = Number(listing.dataset.pageSize) || 20;
  // As in the theme, the first collection page leaves room for the breakers' columns.
  const firstPageSize = isSearch ? pageSize : Math.max(1, pageSize - (Number(listing.dataset.breakerSpace) || 0));
  // The collection's own default order (e.g. newest first) applies until a visitor picks one.
  const defaultSort = listing.dataset.defaultSort || '';
  const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  const formatCount = (n) => Number(n).toLocaleString('da-DK');

  /* The filters exactly as Shopify Search & Discovery shows them (Admin → Search & Discovery → Filters, read
     2026-09-30): same order, labels and value groups. A group's URL value is its Shopify id, as on the live store,
     so links and the server-rendered fallback keep working; choosing a group selects all its values. Keep this in
     step when the filters change in Search & Discovery. */
  const G = 'gid://shopify/FilterSettingGroup/';
  const group = (label, id, members) => ({ label, value: G + id, members });
  const plain = (label) => ({ label, value: label, members: [label] });

  const FACETS = [
    {
      param: 'filter.p.m.custom.malgruppe', heading: 'Målgruppe', key: `${cfg.shop}_custom.malgruppe`, others: true,
      entries: ['Baby', 'Børn', 'Dame', 'Herre', 'Unisex', 'Voksen'].map(plain),
    },
    {
      param: 'filter.p.product_type', heading: 'Produkttype', key: 'ProductType', others: true,
      entries: [
        group('Spillertrøje', 387416404, ['Spillershorts', 'Spillerstrømper', 'Spillertrøje', 'Spillertryk']),
        group('Træningstøj', 387481940, ['Træningsjakke', 'Træningsoverdel', 'Træningsunderdel']),
        group('Tøj', 387514708, ['Badekåbe', 'Bukser', 'Hoodie', 'Jakke', 'Overdel', 'Overdel - Barn', 'Overdel - Voksen', 'Polo', 'Shorts', 'Shorts - Voksen', 'Søvntøj', 'Strik', 'Sweatshirt', 'T-shirt', 'Underdel']),
        plain('Baby'),
        group('Bolig & Indretning', 387350868, ['Bog', 'Diverse', 'Diverse - Børn', 'Drikkevarer', 'Fest & Dekoration', 'Flag', 'Håndklæde', 'Indretning', 'Køkken & Drikke', 'Kontor & Skole', 'Plakat', 'Sengetøj']),
        group('Tilbehør', 387449172, ['Bøllehat', 'Cap', 'Drikkeflaske', 'Halstørklæde', 'Handsker', 'Hue', 'Pung', 'Sæsonartikel', 'Sko', 'Strømper', 'Taske', 'Tilbehør', 'Tilbehør - Børn']),
        group('Gavekort', 387383636, ['Gavekort', 'GIST_GIFT_CARD']),
        plain('Undertøj'),
      ],
    },
    {
      // In Relewise, sizes are the first variant option; anything outside these entries (gift-card amounts,
      // other options) is not a size and stays out, as on the live store.
      param: 'filter.v.option.størrelse', aliases: ['filter.v.option.size'], heading: 'Størrelse', size: true,
      entries: [
        group('One Size', 387154260, ['Default Title', 'One Size', 'Onesize', 'OneSize']),
        group('XS', 386662740, ['XS', 'XS/S', 'XXS', 'XXXS']),
        group('Small', 386695508, ['S', 'S-M', 'S/M']),
        group('Medium', 386728276, ['M', 'M/L']),
        group('Large', 386761044, ['L', 'L-XL', 'L/XL']),
        plain('XL'),
        group('2XL', 386793812, ['2XL', 'XL/XXL', 'XXL']),
        group('3XL', 386826580, ['3Xl', '3XL']),
        plain('4XL'),
        plain('5XL'),
        group('62/68', 386859348, ['62', '62/68', '68']),
        group('74/80', 386892116, ['72', '74/80', '80']),
        group('86/92', 386924884, ['86', '86/92', '90', '92']),
        group('98/104', 386957652, ['100', '104', '2 år', '3-4 år', '90/100', '98/104']),
        group('110/116', 386990420, ['104/116', '110', '110/116', '110/120', '116', '3-5 år', '5-6 år']),
        group('122/128', 387023188, ['120', '122/128', '128', '6-8 år', '7-8 år']),
        group('134/140', 387055956, ['128/140', '130', '130/140', '134/140', '140', '9- 10 år', '9-10 år', '9-11 år']),
        group('146/152', 387088724, ['11-12 år', '146/152', '150', '152']),
        group('158/164', 387121492, ['12-14 år', '13-14 år', '150/160', '152/164', '158/164', '160', '164', '170', '176']),
        plain('28/30'),
        plain('31/33'),
        plain('34/36'),
        plain('35/38'),
        group('37/39', 387219796, ['35-40', '37/39']),
        plain('39/42'),
        group('42/45', 387252564, ['41-47', '42/45']),
        plain('46/48'),
        group('Sko', 387187028, ['30', '31', '32', '33', '34', '35', '35.5', '36', '37', '37.5', '38', '38 3/4', '39', '39.5', '40', '40 3/4', '41', '41.5', '42', '43', '43.5', '44', '44.5', '45', '45 3/4', '46', '46.5', '47', '48']),
        group('40/42', 387285332, ['40/42', '41-47', '42/45']),
        group('43/45', 387318100, ['43/45', '43/46']),
        plain('6+ Måneder'),
        plain('0-6 Måneder'),
      ],
    },
    {
      param: 'filter.p.m.custom.aermelaengde', heading: 'Ærmelængde', key: `${cfg.shop}_custom.aermelaengde`, others: true,
      entries: ['Kort', 'Lang'].map(plain),
    },
  ];

  /** A URL value → its entry (a group by id or label; any other value filters on itself). */
  const entryFor = (facet, value) => facet.entries.find((e) => e.value === value || e.label === value) || plain(value);
  const chosenEntries = (facet) => (state.filters[facet.param] || []).map((value) => entryFor(facet, value));

  /* Shopify sort_by values → Relewise sorting; anything else ("Fremhævet" = manual, "Mest relevante") is
     Relewise's own ranking. */
  const relevance = (then) => then.sortByProductRelevance();
  const SORTS = {
    'best-selling': (s) => s.sortByProductPopularity('Descending', relevance),
    'price-ascending': (s) => s.sortByProductAttribute('SalesPrice', 'Ascending', relevance),
    'price-descending': (s) => s.sortByProductAttribute('SalesPrice', 'Descending', relevance),
    'title-ascending': (s) => s.sortByProductAttribute('DisplayName', 'Ascending', relevance),
    'title-descending': (s) => s.sortByProductAttribute('DisplayName', 'Descending', relevance),
    // Shopify's "Dato" sorts use the product's creation date (not when it was last published).
    'created-ascending': (s) => s.sortByProductData(`${cfg.shop}_CreatedAt`, 'Product', 'Ascending', relevance),
    'created-descending': (s) => s.sortByProductData(`${cfg.shop}_CreatedAt`, 'Product', 'Descending', relevance),
  };

  /* ---- State (the URL) ------------------------------------------------------------------ */
  function readState() {
    const params = new URLSearchParams(window.location.search);
    const filters = {};
    for (const facet of FACETS) {
      const values = [facet.param, ...(facet.aliases || [])].flatMap((name) => params.getAll(name));
      if (values.length) filters[facet.param] = [...new Set(values)];
    }
    return {
      term: params.get('q') || '',
      sort: params.get('sort_by') || '',
      page: Math.max(1, parseInt(params.get('page'), 10) || 1),
      filters,
    };
  }

  function writeState(next, replace) {
    const params = new URLSearchParams(window.location.search);
    FACETS.forEach((facet) => [facet.param, ...(facet.aliases || [])].forEach((name) => params.delete(name)));
    Object.entries(next.filters).forEach(([param, values]) => values.forEach((value) => params.append(param, value)));
    if (next.sort) params.set('sort_by', next.sort);
    else params.delete('sort_by');
    if (next.page > 1) params.set('page', next.page);
    else params.delete('page');
    const query = params.toString();
    window.history[replace ? 'replaceState' : 'pushState']({ relewise: true }, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }

  let state = readState();

  /* ---- Request ------------------------------------------------------------------------ */
  async function search(skip, take) {
    const { Relewise } = window;
    const settings = core.settings(isSearch ? core.LOCATIONS.search : core.LOCATIONS.category, await core.user());
    const facetSettings = { alwaysIncludeSelectedInAvailable: true, includeZeroHitsInAvailable: false, take: 200 };
    const builder = new Relewise.ProductSearchBuilder(settings)
      .setSelectedProductProperties({ dataKeys: [core.HANDLE_KEY] })
      .pagination((p) => p.setPageSize(take).setPage(1))
      .facets((f) => FACETS.forEach((facet) => {
        const selected = [...new Set(chosenEntries(facet).flatMap((entry) => entry.members))];
        if (facet.size) f.addVariantSpecificationFacet('Option1', selected.length ? selected : null, facetSettings);
        else f.addProductDataStringValueFacet(facet.key, 'Product', selected.length ? selected : null, 'Or', facetSettings);
      }));
    builder
      .filters((f) => core.catalogFilters(f))
      .relevanceModifiers((r) => core.buryUnavailable(r));
    if (isSearch) builder.setTerm(state.term);
    else if (cfg.collectionId) builder.filters((f) => f.addProductCategoryIdFilter('ImmediateParent', [cfg.collectionId]));
    const sort = SORTS[state.sort || defaultSort];
    if (sort) builder.sorting(sort);

    const request = builder.build();
    // Exact offsets: collection pages after the first are full pages.
    request.skip = skip;
    request.take = take;
    request.settings.variantRequestSettings = { maxVariantsPerProduct: 1, sorting: 'GroupedByProduct' };
    return core.searcher().searchProducts(request);
  }

  /* ---- Rendering ---------------------------------------------------------------------- */
  const FIXED_TILES = '.product-item--breaker, .breaker_item, .product-item--featured-image';
  const fixedTiles = isSearch ? [] : [...grid.children].map((el, index) => ({ el, index })).filter(({ el }) => el.matches(FIXED_TILES));

  async function renderCards(results, append) {
    const cards = await core.cards(core.handles(results), {
      collection: isSearch ? '' : listing.dataset.collectionHandle,
      sizes: listing.dataset.cardSizes,
    });
    const holder = document.createElement('div');
    holder.innerHTML = cards.map((card, i) => core.fillTokens(card.html, { anchor: `#${grid.id}`, delay: (i % 4) * 100 })).join('');
    holder.querySelectorAll('[data-aos]').forEach((el) => el.classList.add('aos-animate'));
    const nodes = [...holder.children];

    if (append) {
      grid.append(...nodes);
    } else {
      grid.replaceChildren(...nodes);
      fixedTiles.forEach(({ el, index }) => grid.insertBefore(el, grid.children[index] || null));
    }
    document.dispatchEvent(new CustomEvent('wishlist:toggleButtonState'));
  }

  /* Products Relewise can't hold (no SKU on any variant, e.g. Shopify Bundles) slot in where Shopify has them: their
     place in the collection's own order, or in Shopify's own results for the term (first page). Only in the listing's
     own view: with a filter or another sort chosen, Relewise can't place them. The page keeps Shopify's size; Relewise
     products pushed off its end come with "Vis flere". */
  let pendingExtras = [];
  let extrasShown = 0;
  let extrasTotal = 0;
  // Collections only: search runs on Relewise alone.
  const extrasView = () => !isSearch && !Object.keys(state.filters).length && !state.sort;
  const loadExtras = () => (extrasView()
    ? core.extras({ collection: listing.dataset.collectionHandle, sizes: listing.dataset.cardSizes })
    : Promise.resolve([]));
  const productTiles = () => [...grid.children].filter((el) => !el.matches(FIXED_TILES));

  function placeExtras(allLoaded) {
    const waiting = [];
    pendingExtras.sort((a, b) => a.position - b.position).forEach((extra) => {
      const tiles = productTiles();
      if (extra.position >= tiles.length && !allLoaded) {
        waiting.push(extra);
        return;
      }
      const holder = document.createElement('div');
      holder.innerHTML = core.fillTokens(extra.html, { anchor: `#${grid.id}` });
      holder.querySelectorAll('[data-aos]').forEach((el) => el.classList.add('aos-animate'));
      const node = holder.firstElementChild;
      if (!node) return;
      node.setAttribute('data-relewise-extra', '');
      grid.insertBefore(node, tiles[extra.position] || null);
      extrasShown += 1;
    });
    pendingExtras = waiting;
  }

  // Collections: as many products as Shopify shows after this many pages; the rest come with "Vis flere".
  function trimToPage() {
    if (isSearch || shownCount >= totalHits) return;
    const expected = firstPageSize + (state.page - 1) * pageSize;
    const extra = productTiles().length - expected;
    if (extra <= 0) return;
    productTiles().filter((el) => !el.hasAttribute('data-relewise-extra')).slice(-extra).forEach((el) => el.remove());
    shownCount -= extra;
  }

  function renderEmpty() {
    const empty = listing.querySelector('template[data-rw-empty]');
    grid.innerHTML = empty ? empty.innerHTML : '';
    document.dispatchEvent(new CustomEvent('relewise:no-results', { detail: { term: state.term } }));
  }

  const facetsHost = listing.querySelector('[data-relewise-facets]');

  function renderFacets(response) {
    if (!facetsHost) return;
    const items = (response.facets && response.facets.items) || [];
    let active = 0;
    facetsHost.innerHTML = FACETS.map((facet, i) => {
      const result = items.find((item) => (facet.size ? item.key === 'Option1' && /VariantSpecification/.test(item.$type) : item.key === facet.key));
      const hits = new Map(((result && result.available) || []).map((v) => [v.value, v.hits]));
      const chosen = chosenEntries(facet);
      const isChosen = (entry) => chosen.some((c) => c.value === entry.value);
      // Entries with products (or chosen), in Search & Discovery's order; then new values, as it adds them.
      const shown = facet.entries.filter((entry) => entry.members.some((m) => hits.get(m) > 0) || isChosen(entry));
      if (facet.others) {
        const known = new Set(facet.entries.flatMap((entry) => entry.members));
        hits.forEach((n, value) => { if (n > 0 && !known.has(value)) shown.push(plain(value)); });
      }
      chosen.forEach((entry) => { if (!shown.some((e) => e.value === entry.value)) shown.push(entry); });
      if (!shown.length) return '';
      active += chosen.length;
      const options = shown.map((entry, j) => {
        const selected = isChosen(entry);
        const id = `rw-filter-${i}-${j}`;
        const itemClass = selected ? 'sidebar__item--active link--remove' : 'link--add';
        return `<li class="sidebar__item ${itemClass}"><radio-swatch>`
          + `<input type="checkbox" name="${escapeHtml(facet.param)}" value="${escapeHtml(entry.value)}" id="${id}"${selected ? ' checked' : ''}>`
          + `<label for="${id}"><span>${escapeHtml(entry.label)}</span></label></radio-swatch></li>`;
      }).join('');
      return `<div class="filter-group${facet.size ? ' filter-group__size' : ''}">`
        + `<div class="filter-group__heading"><span>${escapeHtml(facet.heading)} </span></div>`
        + `<div class="filter-group__body"><div class="filter-group__content"><ul class="collection-nav">${options}</ul></div></div></div>`;
    }).join('');

    listing.querySelectorAll('[data-active-filters]').forEach((el) => {
      el.textContent = active;
      el.classList.toggle('hidden', active < 1);
    });
    // As the theme: no filter groups (e.g. a search without results) → "No filters available".
    const noFilters = listing.querySelector('[data-relewise-no-filters]');
    if (noFilters) noFilters.hidden = facetsHost.children.length > 0;
  }

  function renderCounts(hits, shown) {
    const count = formatCount(hits);
    listing.querySelectorAll('.collection-product-count').forEach((el) => {
      el.textContent = el.textContent.replace(/\d[\d.]*/, count);
    });
    listing.querySelectorAll('.collection__sidebar__buttons [data-collection-sidebar-close] span').forEach((el) => {
      el.textContent = el.textContent.replace(/\d[\d.]*/, count);
    });
    const caption = listing.querySelector('[data-rw-caption]');
    if (caption) caption.textContent = (hits ? caption.dataset.rwCount.replace('999999', count) : caption.dataset.rwEmpty).replace('__TERMS__', state.term);
    const current = listing.querySelector('.current-products');
    const total = listing.querySelector('.total-products');
    if (current) current.textContent = formatCount(shown);
    if (total) total.textContent = count;
  }

  let totalHits = 0;
  let shownCount = 0;

  function renderMore() {
    const button = listing.querySelector('[data-relewise-load-more]');
    if (button) button.closest('.load-more__container').hidden = shownCount >= totalHits;
  }

  function renderPagination() {
    const nav = listing.querySelector('[data-rw-pagination]');
    if (!nav) return;
    const pages = Math.ceil(totalHits / pageSize);
    if (pages < 2) {
      nav.innerHTML = '';
      return;
    }
    const link = (page, cls, inner, title) => `<a class="${cls}" href="?page=${page}" data-rw-page="${page}" title="${escapeHtml(title)}">${inner}</a>`;
    const prevTpl = nav.dataset.prev;
    const nextTpl = nav.dataset.next;
    const parts = [];
    for (let page = 1; page <= pages; page++) {
      if (page === state.page) parts.push(`<span class="pagination-custom__page pagination-custom__page--active">${page}</span>`);
      else if (page === 1 || page === pages || Math.abs(page - state.page) <= 2) parts.push(link(page, 'pagination-custom__page', page, page));
      else if (parts[parts.length - 1] !== '…') parts.push('…');
    }
    nav.innerHTML = `<nav class="pagination-custom">`
      + (state.page > 1 ? link(state.page - 1, 'pagination-custom__prev', prevTpl, '') : `<span class="pagination-custom__prev is-hidden">${prevTpl}</span>`)
      + `<div class="pagination-custom__inner">${parts.map((p) => (p === '…' ? '<span class="pagination-custom__sep">…</span>' : p)).join('')}</div>`
      + (state.page < pages ? link(state.page + 1, 'pagination-custom__next', nextTpl, '') : `<span class="pagination-custom__next is-hidden">${nextTpl}</span>`)
      + '</nav>';
  }

  function renderSort() {
    // As Shopify: a search counts as sorted by "Relevans" until another order is picked; a collection shows
    // "Sorter efter" until one is.
    const current = state.sort || (isSearch ? 'relevance' : '');
    listing.querySelectorAll('popout-select[data-sort-enabled]').forEach((popout) => {
      popout.classList.toggle('is-active', !!current);
      popout.dataset.sortEnabled = current;
    });
    listing.querySelectorAll('[data-sort-link]').forEach((link) => {
      const active = link.dataset.value === current;
      link.closest('li')?.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
      if (active) listing.querySelectorAll('[data-sort-button-text]').forEach((el) => { el.textContent = link.textContent.trim(); });
    });
  }

  /* ---- Run -------------------------------------------------------------------------------- */
  let sequence = 0;

  async function run({ scroll = false } = {}) {
    const mine = ++sequence;
    listing.classList.add('is-loading');
    try {
      // Collections append pages ("Vis flere"): landing on ?page=3 loads pages 1–3 in one request.
      const [response, extraList] = await Promise.all([
        isSearch ? search((state.page - 1) * pageSize, pageSize) : search(0, firstPageSize + (state.page - 1) * pageSize),
        loadExtras(),
      ]);
      if (mine !== sequence) return;
      // A search-term redirect configured in My Relewise (gavekort, retur, billetter …).
      const redirect = isSearch && state.page === 1 && response.redirects && response.redirects[0];
      if (redirect) {
        window.location.replace(redirect.destination);
        return;
      }
      totalHits = response.hits || 0;
      const results = response.results || [];
      shownCount = results.length;
      extrasTotal = extraList.length;
      extrasShown = 0;
      pendingExtras = [...extraList];
      if (results.length) await renderCards(results, false);
      else if (pendingExtras.length) grid.replaceChildren();
      else renderEmpty();
      placeExtras(shownCount >= totalHits);
      trimToPage();
      listing.toggleAttribute('data-relewise-empty', !results.length && !extrasShown);
      if (mine !== sequence) return;
      renderFacets(response);
      renderCounts(totalHits + extrasTotal, shownCount + extrasShown);
      renderMore();
      renderPagination();
      renderSort();
      if (scroll) listing.querySelector('[data-products-grid]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      console.warn('Relewise listing unavailable, showing the Shopify result.', error);
    } finally {
      if (mine === sequence) {
        listing.classList.remove('is-loading');
        ready();
      }
    }
  }

  async function loadMore(button) {
    const mine = ++sequence;
    const busy = (on) => {
      button.disabled = on;
      button.querySelector('[load-more-text]')?.classList.toggle('hide', on);
      button.querySelector('[loader-text]')?.classList.toggle('hide', !on);
    };
    busy(true);
    try {
      const response = await search(shownCount, pageSize);
      if (mine !== sequence) return;
      await renderCards(response.results || [], true);
      state.page += 1;
      shownCount += (response.results || []).length;
      placeExtras(shownCount >= totalHits);
      trimToPage();
      writeState(state, true);
      renderCounts(totalHits + extrasTotal, shownCount + extrasShown);
      renderMore();
    } catch (error) {
      const params = new URLSearchParams(window.location.search);
      params.set('page', state.page + 1);
      window.location.href = `${window.location.pathname}?${params}`;
    } finally {
      busy(false);
    }
  }

  function update(changes, options) {
    state = { ...state, ...changes };
    writeState(state, false);
    run(options);
  }

  /* ---- Interactions -------------------------------------------------------------------------- */
  let filterTimer = null;
  facetsHost?.addEventListener('change', (event) => {
    if (!event.target.matches('input[type="checkbox"]')) return;
    clearTimeout(filterTimer);
    filterTimer = setTimeout(() => {
      const filters = {};
      facetsHost.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
        (filters[input.name] = filters[input.name] || []).push(input.value);
      });
      update({ filters, page: 1 });
    }, 300);
  });

  // Capture phase: the theme's own sort/filter/load-more handlers would reload Shopify's result.
  document.addEventListener('click', (event) => {
    const sortLink = event.target.closest('[data-relewise-listing] [data-sort-link]');
    if (sortLink) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const popout = sortLink.closest('popout-select');
      popout?.querySelector('[data-popout-toggle]')?.setAttribute('aria-expanded', 'false');
      popout?.querySelector('[data-popout-list]')?.classList.remove('popout-list--visible');
      update({ sort: sortLink.dataset.value, page: 1 });
      return;
    }
    const reset = event.target.closest('[data-relewise-listing] [data-filter-update-url], [data-relewise-listing] [data-rw-reset]');
    if (reset) {
      event.preventDefault();
      event.stopImmediatePropagation();
      update({ filters: {}, page: 1 });
      return;
    }
    const page = event.target.closest('[data-relewise-listing] [data-rw-page]');
    if (page) {
      event.preventDefault();
      update({ page: Number(page.dataset.rwPage) }, { scroll: true });
      return;
    }
    const more = event.target.closest('[data-relewise-listing] [data-relewise-load-more]');
    if (more) {
      event.preventDefault();
      event.stopImmediatePropagation();
      loadMore(more);
    }
  }, true);

  window.addEventListener('popstate', () => {
    state = readState();
    run();
  });

  run();
})();
